/* sections/header.liquid runtime — Phase 5 plans 05-01 + 05-02 + 05-03.
 *
 * Single-file IIFE, no library dependencies. Mirrors assets/section-collection.js
 * shape. Originally scaffolded in plan 05-01 (Wave 1) as a no-op; plan 05-02
 * (Wave 2) added the desktop catnav dropdown disclosure controller; this plan
 * (05-03 / Wave 3) appends the mobile <dialog id="nav-drawer"> drawer controller.
 *
 * Wave 1 (plan 05-01): file-header + 'use strict' + HTMLDialogElement feature-test.
 * Wave 2 (plan 05-02): desktop disclosure controller. Hover-intent (300ms /
 *                      D-N3 amendment), click toggle on parent <a> (parent
 *                      follows the link on click; keyboard opens via
 *                      Enter/Space/Down without following; Esc closes;
 *                      click-outside closes; focus-out closes; aria-expanded +
 *                      hidden toggling).
 * Wave 3 (this plan):  mobile drawer controller. <dialog id="nav-drawer">.showModal()
 *                      with .nav-hamburger trigger, backdrop-click close,
 *                      .nav-drawer__close button, native close event sync of
 *                      aria-expanded, explicit .nav-drawer__close.focus() after
 *                      showModal() for deterministic focus landing. Legacy
 *                      fallback hides hamburger via aria-hidden + display:none
 *                      on unsupported browsers.
 *
 * Decisions: D-N1 (hybrid clickable parent <a> + disclosure trigger; W3C ARIA
 *            Disclosure Pattern, no menubar/menuitem/haspopup), D-N3
 *            (300ms hover-intent + 150ms hover-out grace), D-N5 (native
 *            <dialog>), D-N8 (vanilla IIFE, no <details-disclosure>),
 *            D-X6 (observe + react, never fork vendor code).
 *
 * Loaded UNCONDITIONALLY from layout/theme.liquid (D-X1).
 */

(function () {
  'use strict';

  var dialogSupported = typeof HTMLDialogElement === 'function';

  // -- Wave 2: desktop catnav disclosure controller -------------------------
  // Gate hover behavior to devices that report (hover: hover) and (pointer: fine)
  // so touch laptops stay on click-only.
  var hoverGate = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  var HOVER_OPEN_MS = 300;   // D-N3 amendment (Baymard 300-500ms flicker-safe)
  var HOVER_CLOSE_MS = 150;  // hover-out grace; allows mouse to traverse parent then panel

  var openTimers = new WeakMap();
  var closeTimers = new WeakMap();
  var openItem = null;       // currently open <li.cn-item--has-children>

  function getPanel(item) {
    var trigger = item && item.querySelector('a[data-cn-trigger][aria-controls]');
    if (!trigger) return null;
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  function openDisclosure(item) {
    if (!item) return;
    if (openItem && openItem !== item) closeDisclosure(openItem);
    var trigger = item.querySelector('a[data-cn-trigger][aria-controls]');
    var panel = getPanel(item);
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');
    openItem = item;
  }

  function closeDisclosure(item) {
    if (!item) return;
    var trigger = item.querySelector('a[data-cn-trigger][aria-controls]');
    var panel = getPanel(item);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('hidden', '');
    if (openItem === item) openItem = null;
  }

  function clearTimers(item) {
    if (openTimers.has(item)) { clearTimeout(openTimers.get(item)); openTimers.delete(item); }
    if (closeTimers.has(item)) { clearTimeout(closeTimers.get(item)); closeTimers.delete(item); }
  }

  // Wire all has-children items found at DOMContentLoaded. Header is rendered
  // server-side so no MutationObserver needed.
  function wireDisclosure() {
    var items = document.querySelectorAll('nav.catnav .cn-item--has-children');
    items.forEach(function (item) {
      var trigger = item.querySelector('a[data-cn-trigger][aria-controls]');
      if (!trigger) return;

      // Mouse hover. Always wired (no matchMedia gate). On true touch devices
      // mouseenter does not fire, so the hover path is harmless. On hybrid
      // touch laptops where matchMedia lies (Windows 11 with touchscreen +
      // mouse common case), hover still works as expected.
      item.addEventListener('mouseenter', function () {
        clearTimers(item);
        openTimers.set(item, setTimeout(function () { openDisclosure(item); }, HOVER_OPEN_MS));
      });
      item.addEventListener('mouseleave', function () {
        clearTimers(item);
        closeTimers.set(item, setTimeout(function () { closeDisclosure(item); }, HOVER_CLOSE_MS));
      });

      // Click on parent <a>: hybrid pattern per D-N1.
      // First click on a CLOSED panel: open the panel + preventDefault. The
      // panel's "Alle X ansehen" overview link is the navigation path.
      // Click on an ALREADY-OPEN panel: allow default navigation. This makes
      // the parent <a> reachable for desktop mouse users via two clicks AND
      // for touch users via tap-to-open then tap-overview-to-navigate. Hover
      // users get the existing intent-delay open + click-navigates flow if
      // they click while the hover-open panel is showing.
      trigger.addEventListener('click', function (e) {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (!isOpen) {
          e.preventDefault();
          clearTimers(item);
          openDisclosure(item);
        }
        // Else: panel already open (via hover or prior click) -> default <a>
        // navigation fires and the page follows the link.
      });

      // Keyboard: Enter / Space / Down arrow opens the panel WITHOUT navigating
      // (preventDefault) when the panel is closed. Space + Down always open;
      // Enter opens only if not already open (so a second Enter follows the link).
      trigger.addEventListener('keydown', function (e) {
        var key = e.key;
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (key === 'Escape') {
          if (isOpen) { e.preventDefault(); closeDisclosure(item); trigger.focus(); }
          return;
        }
        if (key === 'ArrowDown' || key === 'Down' || key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          openDisclosure(item);
          var firstLink = getPanel(item) && getPanel(item).querySelector('a');
          if (firstLink) firstLink.focus();
          return;
        }
        if (key === 'Enter' && !isOpen) {
          e.preventDefault();
          openDisclosure(item);
          var firstLink = getPanel(item) && getPanel(item).querySelector('a');
          if (firstLink) firstLink.focus();
          return;
        }
        // Enter on already-open panel falls through to default <a> click then follows the link.
      });
    });
  }

  // Click outside any open panel + trigger group closes the panel.
  document.addEventListener('click', function (e) {
    if (!openItem) return;
    if (e.target.closest && e.target.closest('nav.catnav .cn-item--has-children')) return;
    closeDisclosure(openItem);
  });

  // Focus moves outside the open .cn-item closes the panel.
  // focusin bubbles, so a single document-level listener works.
  document.addEventListener('focusin', function (e) {
    if (!openItem) return;
    if (openItem.contains(e.target)) return;
    closeDisclosure(openItem);
  });

  // Escape on document closes any open panel and restores focus to the trigger.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openItem) return;
    var trigger = openItem.querySelector('a[data-cn-trigger]');
    closeDisclosure(openItem);
    if (trigger) trigger.focus();
  });

  // Run wireDisclosure once DOM is ready. Header is rendered SSR; no SPA refresh.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireDisclosure);
  } else {
    wireDisclosure();
  }

  // -- Wave 3 (plan 05-03): mobile drawer controller ------------------------
  // Native <dialog id="nav-drawer">.showModal()/close() mirrors Phase 4
  // assets/section-collection.js. Browser-managed focus trap, inert
  // background, and Esc-close are all native to <dialog>.
  //
  // Focus management: native showModal() auto-focus is UA-defined (some UAs
  // focus the dialog box itself, others the first focusable child). To make
  // focus deterministic across Chrome/Safari/Firefox, we explicitly focus
  // .nav-drawer__close immediately after showModal() so keyboard users always
  // land on the close button. Mirrors Phase 4 D-M1 + W3C WCAG H102 guidance.
  var navDrawer = document.getElementById('nav-drawer');
  var drawerSupported = navDrawer
    && typeof HTMLDialogElement === 'function'
    && typeof navDrawer.showModal === 'function';

  if (navDrawer && !drawerSupported) {
    // Legacy fallback (pre-iOS-15.4 browsers): hide hamburger + drawer entirely.
    document.querySelectorAll('[data-nav-drawer-trigger]').forEach(function (btn) {
      btn.setAttribute('aria-hidden', 'true');
      btn.style.display = 'none';
    });
    navDrawer.setAttribute('aria-hidden', 'true');
    navDrawer.style.display = 'none';
  }

  if (drawerSupported) {
    // Sync hamburger aria-expanded from the dialog's open state via the
    // native `close` event (fires on .close(), backdrop click, Esc, browser-handled).
    // Also explicitly restore focus to the trigger. Native <dialog> auto-restoration
    // is unreliable when focus was moved inside the dialog after showModal() (see
    // T-05-03-05). Explicit restore makes State 6 of the 6/6 sweep deterministic.
    navDrawer.addEventListener('close', function () {
      var triggers = document.querySelectorAll('[data-nav-drawer-trigger]');
      triggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      if (triggers[0]) triggers[0].focus();
    });

    // Delegated click. Open via [data-nav-drawer-trigger], close via
    // .nav-drawer__close or [data-nav-drawer-close], close via backdrop click
    // (event.target IS the dialog).
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-nav-drawer-trigger]');
      if (trigger) {
        event.preventDefault();
        if (!navDrawer.open) {
          navDrawer.showModal();
          trigger.setAttribute('aria-expanded', 'true');
          // Explicit focus-set: native showModal() auto-focus is UA-defined.
          // Move focus deterministically to the close button so keyboard users
          // always know where focus landed. Defensive null-check.
          var closeBtn = navDrawer.querySelector('.nav-drawer__close');
          if (closeBtn) closeBtn.focus();
        }
        return;
      }

      var closer = event.target.closest && event.target.closest('[data-nav-drawer-close], .nav-drawer__close');
      if (closer) {
        event.preventDefault();
        navDrawer.close();
        return;
      }

      // Backdrop click: event.target is the dialog itself (clicking the
      // dialog's padding area outside the .nav-drawer__body content).
      if (event.target === navDrawer) {
        navDrawer.close();
      }
    });
  }

  // Sanity log, visible in DevTools console after the push.
  if (window && window.console && window.console.debug) {
    window.console.debug('[section-header.js] Wave 3 ready; hover gate:', hoverGate, '; drawer supported:', drawerSupported);
  }
})();
